import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import AgentKPICards from './AgentKPICards';
import AgentPerformanceChart from './AgentPerformanceChart';
import AgentRecentOrdersTable from './AgentRecentOrdersTable';
import AgentAvailableOrdersPanel from './AgentAvailableOrdersPanel';
import AgentCommissionSummary from './AgentCommissionSummary';
import AgentNotifications from './AgentNotifications';
import AgentSponsorshipCard from './AgentSponsorshipCard';
import api from '../../api/axios';

function formatCurrency(value) {
  if (typeof value === 'number') return `₹${value.toLocaleString('en-IN')}`;
  if (value === 0) return '₹0';
  return value || '—';
}

function timeAgo(timestamp) {
  if (!timestamp) return 'Just now';
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Calculate commission amount from order items based on category-wise rates
// Only uses values from database - no fallbacks or defaults
function calculateItemCommission(item) {
  if (!item) return 0;
  
  // Use only database values - no mock or default rates
  let commissionRate = item.commissionRate;
  const commissionType = item.commissionType;
  
  // If rate or type is missing from database, we cannot calculate
  if (commissionRate === undefined || commissionRate === null || commissionType === undefined) {
    return 0;
  }
  
  // Convert to number if it's a string (from API)
  commissionRate = Number(commissionRate);
  
  // Validate it's a valid number
  if (isNaN(commissionRate)) {
    return 0;
  }
  
  const baseAmount = (item.price || 0) * (item.qty || 1);
  
  if (commissionType === 'percentage') {
    return Number((baseAmount * commissionRate).toFixed(2));
  }
  // fixed commission type
  return Number(commissionRate.toFixed(2));
}

// Calculate total commission for an order from all items
function calculateOrderCommission(order) {
  if (!order.items || order.items.length === 0) {
    // Only use agentCommission if it exists in database - no fallback calculations
    return Number(order.agentCommission?.amount ?? 0);
  }
  const totalCommission = order.items.reduce((sum, item) => {
    return sum + calculateItemCommission(item);
  }, 0);
  return totalCommission;
}

function buildTrendData(orders) {
  const now = new Date();
  const buildRange = (days) => {
    const buckets = Array.from({ length: days }).map((_, index) => {
      const date = new Date(now);
      date.setDate(now.getDate() - (days - index - 1));
      return {
        date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        completed: 0,
        earnings: 0,
        acceptance: 0,
      };
    });

    orders.forEach((order) => {
      if (order.deliveryPhase?.status !== 'DELIVERED') return;
      const createdAt = new Date(order.createdAt || order.agent?.assignedAt || order.updatedAt || now);
      const label = createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
      const bucket = buckets.find((item) => item.date === label);
      if (bucket) {
        bucket.completed += 1;
        bucket.earnings += calculateOrderCommission(order);
      }
    });

    return buckets.map((bucket) => ({
      ...bucket,
      acceptance: bucket.completed > 0 ? 80 : 0,
    }));
  };

  return {
    today: buildRange(1),
    '7d': buildRange(7),
    '30d': buildRange(30),
    '90d': buildRange(90),
  };
}

// Enrich orders with category commission rates from products
// Prioritizes subcategory commission rates over main category
async function enrichOrdersWithCategoryCommissions(orders) {
  if (!orders || orders.length === 0) return orders;
  
  try {
    // Cache for products and categories to avoid duplicate API calls
    const productCache = {};
    const categoryCache = {};
    
    // Helper to fetch category commission rate
    const getCategoryCommissionRate = async (categoryId) => {
      if (!categoryId) return null;
      if (categoryCache[categoryId]) return categoryCache[categoryId];
      
      try {
        const { data } = await api.get(`/categories/${categoryId}`).catch(() => ({ data: {} }));
        if (data.commissionRate !== undefined) {
          categoryCache[categoryId] = {
            rate: Number(data.commissionRate),
            type: data.commissionType || 'percentage',
          };
          return categoryCache[categoryId];
        }
      } catch (error) {
        console.warn(`Could not fetch category ${categoryId}:`, error);
      }
      return null;
    };
    
    // Helper to fetch product and get commission from subcategory/category
    const getProductCommissionRate = async (productId) => {
      if (!productId) return null;
      if (productCache[productId]) return productCache[productId];
      
      try {
        const { data } = await api.get(`/products/${productId}`).catch(() => ({ data: {} }));
        
        // Priority 1: Subcategories (use first subcategory)
        if (data.subcategories && data.subcategories.length > 0) {
          const subCategoryId = data.subcategories[0];
          const commissionData = await getCategoryCommissionRate(subCategoryId);
          if (commissionData) {
            productCache[productId] = commissionData;
            return commissionData;
          }
        }
        
        // Priority 2: Main category
        if (data.category) {
          const commissionData = await getCategoryCommissionRate(data.category);
          if (commissionData) {
            productCache[productId] = commissionData;
            return commissionData;
          }
        }
      } catch (error) {
        console.warn(`Could not fetch product ${productId}:`, error);
      }
      return null;
    };
    
    // Enrich orders with commission rates from categories
    // Always fetch current category rates from DB, don't rely on stored item rates
    const enrichedOrders = await Promise.all(orders.map(async (order) => {
      if (!order.items || order.items.length === 0) return order;
      
      const enrichedItems = await Promise.all(order.items.map(async (item) => {
        // Always try to fetch current commission rate from product's category/subcategory
        if (item.productId) {
          const commissionData = await getProductCommissionRate(item.productId);
          if (commissionData) {
            return {
              ...item,
              commissionRate: commissionData.rate,
              commissionType: commissionData.type,
            };
          }
        }
        
        // If fetch fails, return item as-is (fallback to whatever is in item)
        return item;
      }));
      
      return { ...order, items: enrichedItems };
    }));
    
    return enrichedOrders;
  } catch (error) {
    console.error('Error enriching orders with category commissions:', error);
    return orders;
  }
}

export default function AgentDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [orders, setOrders] = useState([]);
  const [available, setAvailable] = useState([]);
  const [chartRange, setChartRange] = useState('7d');
  const [chartData, setChartData] = useState({ '7d': [], '30d': [], '90d': [], today: [] });
  const [notifications, setNotifications] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState({});
  const [sponsorshipStatus, setSponsorshipStatus] = useState("NONE");
  const [agentId, setAgentId] = useState(null);
  const navigate = useNavigate();

  const handleOpenOrderDetails = (orderId) => {
    if (orderId) {
      navigate(`/agent/orders/${orderId}`);
    }
  };

  const handleKpiClick = (id) => {
    if (['available', 'assigned', 'completed', 'pending', 'acceptance', 'lost', 'winrate'].includes(id)) {
      navigate('/agent/orders');
    }
  };

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      setLoading(true);
      try {
        const [profileRes, pendingRes, ordersRes] = await Promise.all([
          api.get('/agent/profile').catch(() => ({ data: { profile: null } })),
          api.get('/agent/notifications/pending').catch(() => ({ data: { notifications: [], count: 0 } })),
          api.get('/agent/orders/accepted').catch(() => ({ data: { orders: [], count: 0 } })),
        ]);

        if (!mounted) return;

        const profile = profileRes.data?.profile;
        const pendingOrders = pendingRes.data?.notifications || [];
        let allOrders = ordersRes.data?.orders || [];
        
        // Enrich orders with category commission rates from database
        allOrders = await enrichOrdersWithCategoryCommissions(allOrders);

        const normalizeStatus = (status) => String(status || '').toUpperCase();
        const isCompletedStatus = (status) => ['COMPLETED', 'DELIVERED'].includes(normalizeStatus(status));

        const acceptedOrders = allOrders.filter(order => !isCompletedStatus(order.deliveryPhase?.status));
        const completedOrders = allOrders.filter(order => isCompletedStatus(order.deliveryPhase?.status));

        // Store sponsorship status from profile
        const sponsorshipStatus = profile?.sponsorshipStatus || "NONE";
        const agentId = profile?._id;

        const sumByDays = (days) => {
          const nowMs = Date.now();
          return allOrders.reduce((sum, order) => {
            if (order.deliveryPhase?.status !== 'DELIVERED') return sum;
            const deliveredAt = new Date(order.deliveryPhase?.deliveredAt || order.createdAt || order.agent?.assignedAt || order.updatedAt || nowMs);
            const ageDays = (nowMs - deliveredAt.getTime()) / 86400000;
            const amount = calculateOrderCommission(order);
            return ageDays <= days ? sum + amount : sum;
          }, 0);
        };

        const commissionSummaryData = {
          today: formatCurrency(sumByDays(1)),
          week: formatCurrency(sumByDays(7)),
          month: formatCurrency(sumByDays(30)),
        };

        setKpi({
          available: pendingRes.data?.count ?? pendingOrders.length,
          assigned: acceptedOrders.length,
          completed: completedOrders.length,
          todayCommission: commissionSummaryData.today,
          acceptance:
            profile?.acceptanceRate ??
            (acceptedOrders.length + pendingOrders.length > 0
              ? Math.round((acceptedOrders.length / (acceptedOrders.length + pendingOrders.length)) * 100)
              : 0),
          lost: profile?.ordersLost ?? 0,
          winrate: profile?.winRate ?? 0,
        });

        setCommissionSummary(commissionSummaryData);
        setSponsorshipStatus(sponsorshipStatus);
        setAgentId(agentId);

        const getCustomerName = (user) => {
          if (!user) return '-';
          if (typeof user === 'string') return user;
          const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
          return name || user.username || '-';
        };

        const getVendorName = (vendor) => {
          if (!vendor) return '-';
          if (typeof vendor === 'string') return vendor;
          if (vendor.store?.businessName) return vendor.store.businessName;
          if (vendor.businessName) return vendor.businessName;
          if (vendor.name) return vendor.name;
          if (vendor.vendorName) return vendor.vendorName;
          if (vendor._id) return String(vendor._id);
          if (typeof vendor.toString === 'function' && vendor.toString() !== '[object Object]') {
            return vendor.toString();
          }
          return '-';
        };

        const getOrderVendor = (order) => {
          const item = order.items?.find((item) => item.vendorId || item.vendor);
          return item?.vendorId || item?.vendor || order.items?.[0]?.vendorId || order.items?.[0]?.vendor;
        };

        setOrders(
          allOrders.map((order) => ({
            id: order._id,
            customer: getCustomerName(order.userId),
            vendor: getVendorName(getOrderVendor(order)),
            value: formatCurrency(order.total),
            commission: order.deliveryPhase?.status === 'DELIVERED' 
              ? formatCurrency(calculateOrderCommission(order)) 
              : '₹0',
            status: order.deliveryPhase?.status || order.status || 'Unknown',
            acceptedDate: order.agent?.assignedAt
              ? new Date(order.agent.assignedAt).toLocaleDateString()
              : '-',
          }))
        );

        setAvailable(
          pendingOrders.map((order) => {
            // For pending notifications, calculate commission from items if available
            const commission = order.items && order.items.length > 0
              ? calculateOrderCommission({ items: order.items })
              : Number(order.agentCommission?.amount ?? order.commission ?? order.totalAmount * 0.1 ?? null);
            return {
              id: order.orderId,
              vendor: order.vendorName || 'Unknown Vendor',
              distance: order.distanceKm ? `${order.distanceKm.toFixed(1)} km` : 'N/A',
              commission: formatCurrency(commission),
              totalAmount:
                order.totalAmount != null ? formatCurrency(order.totalAmount) : null,
              time: timeAgo(order.sentAt),
            };
          })
        );

        setNotifications(
          pendingOrders.map((order) => ({
            id: order.orderId,
            title: `New order available near ${order.vendorCity || 'your area'}`,
            time: timeAgo(order.sentAt),
            read: order.isExpired || false,
          }))
        );

        setChartData(buildTrendData(allOrders));
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const handleAccept = async (order) => {
    setAvailable((prev) => prev.filter((o) => o.id !== order.id));
    setOrders((prev) => [
      {
        ...order,
        id: order.id,
        customer: '-',
        vendor: order.vendor,
        value: order.totalAmount || '—',
        commission: order.commission || '—',
        status: 'Assigned',
        acceptedDate: new Date().toLocaleDateString(),
      },
      ...prev,
    ]);

    try {
      await api.post(`/agent/notifications/${order.id}/accept`);
    } catch (error) {
      console.error('Accept order failed:', error);
    }
  };

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" fontWeight={700}>Agent Dashboard</Typography>
          <AgentSponsorshipCard 
            agentId={agentId} 
            sponsorshipStatus={sponsorshipStatus} 
            onApplied={() => {
              // We need to call the fetchData function defined inside useEffect
              // Since it's not available in this scope, we'll move it outside or use a state-based refresh
              // For now, we'll use a window.location.reload() or a simple state update if we can't move the function
              window.location.reload();
            }} 
          />
        </Box>
        <Typography color="text.secondary" sx={{ mb: 3 }}>Manage orders, track earnings, and monitor your performance.</Typography>

        <AgentKPICards data={kpi} loading={loading} onCardClick={handleKpiClick} />

      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Recent Orders</Typography>
        <Typography color="text.secondary">
          Review your latest orders and their current status.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <AgentRecentOrdersTable
            orders={orders}
            onView={(o) => handleOpenOrderDetails(o.id)}
            onTrack={(o) => handleOpenOrderDetails(o.id)}
          />

          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight={700}>Performance</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Track earnings trends and order revenue over time.
            </Typography>
            <AgentPerformanceChart data={chartData} range={chartRange} onRangeChange={setChartRange} />
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight={700}>Commission</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Today's commissions, weekly totals, and monthly performance.
            </Typography>
            <AgentCommissionSummary summary={commissionSummary} />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 100 }}>
            <AgentNotifications items={notifications} onItemClick={(item) => handleOpenOrderDetails(item.id)} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}